import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import {
  Plus, X, Trash2, Download, Edit2, Copy, Send, FileSignature, Receipt,
  CheckCircle2, Search, AlertTriangle, Mail, Filter, ChevronLeft, ChevronRight,
  UserPlus,
} from 'lucide-react';
import RowActions from '../components/RowActions';
import { downloadBlob } from '../utils/download';
import { useConfirm } from '../components/ConfirmDialog';

const STATI = {
  bozza: { label: 'Bozza', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  inviato: { label: 'Inviato', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  pagato: { label: 'Pagato', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  fatturato: { label: 'Fatturato', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  annullato: { label: 'Annullato', color: 'bg-red-100 text-red-700 border-red-300' },
};

const fmt = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ContiPage({ onNavigate }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 25;
  const [loading, setLoading] = useState(false);
  const [filtroStato, setFiltroStato] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showConverti, setShowConverti] = useState(null);
  const [showPagamento, setShowPagamento] = useState(null);
  const [showEmail, setShowEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(LIMIT) });
      if (filtroStato) params.append('stato', filtroStato);
      if (search) params.append('q', search);
      const { data } = await API.get(`/api/conti?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [skip, filtroStato, search]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setShowForm(true); };

  const duplica = (c) => {
    setEditing({
      ...c,
      id: null,
      numero_conto: '',
      stato: 'bozza',
      fattura_id: null,
      fattura_numero: null,
      oggetto: `${c.oggetto || ''} (copia)`,
    });
    setShowForm(true);
  };

  const downloadPdf = async (c) => {
    try {
      await downloadBlob(
        API.get(`/api/conti/${c.id}/pdf`, { responseType: 'blob' }),
        `Conto_${c.numero_conto}.pdf`,
        'application/pdf'
      );
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const annulla = async (c) => {
    if (!await confirm({ title: 'Annulla Conto', message: `Annullare il conto ${c.numero_conto}? Lo stato diventerà "annullato".`, confirmLabel: 'Annulla conto', variant: 'danger' })) return;
    try {
      await API.post(`/api/conti/${c.id}/annulla`);
      load();
    } catch (e) { alert('Errore: ' + (e?.response?.data?.detail || e.message)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(skip / LIMIT) + 1;

  return (
    <div className="space-y-4" data-testid="conti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Receipt size={28} className="text-primary" /> Conti
          </h1>
          <p className="text-sm text-slate-500 mt-1">Operazioni veloci a chiamata: PDF al cliente → poi conversione in fattura.</p>
        </div>
        <button onClick={openNew} data-testid="conti-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white font-bold rounded-md shadow-sm">
          <Plus size={16} /> Nuovo Conto
        </button>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
            placeholder="Cerca per numero, oggetto, cliente…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md"
            data-testid="conti-search-input" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-400" />
          <select value={filtroStato} onChange={(e) => { setFiltroStato(e.target.value); setSkip(0); }}
            className="text-sm border border-slate-200 rounded-md px-2 py-2"
            data-testid="conti-filter-stato">
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center">
            <Receipt size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="font-bold text-slate-600">Nessun conto ancora.</p>
            <p className="text-xs text-slate-400 mt-1">Crea il primo conto per un lavoro a chiamata.</p>
            <button onClick={openNew} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white font-bold rounded-md text-sm">
              <Plus size={14} /> Nuovo Conto
            </button>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="conti-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-xs uppercase tracking-wide text-slate-600">
                  <th className="text-left px-3 py-2">N°</th>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Cliente</th>
                  <th className="text-left px-3 py-2">Oggetto</th>
                  <th className="text-right px-3 py-2">Totale</th>
                  <th className="text-center px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-primary/40" data-testid={`conto-row-${c.id}`}>
                    <td className="px-3 py-2 font-bold font-mono text-xs">{c.numero_conto}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {c.data_emissione ? new Date(c.data_emissione).toLocaleDateString('it-IT') : '-'}
                    </td>
                    <td className="px-3 py-2">{c.cliente_label || <span className="italic text-slate-400">n/d</span>}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 max-w-[280px] truncate" title={c.oggetto}>{c.oggetto || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmt(c.totale_finale)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${STATI[c.stato]?.color || ''}`}>
                        {STATI[c.stato]?.label || c.stato}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadPdf(c)} title="Scarica PDF" data-testid={`conto-pdf-${c.id}`}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Download size={14} /></button>
                        <button onClick={() => setShowEmail(c)} title="Invia PDF via Email" data-testid={`conto-email-${c.id}`}
                          className="p-1.5 hover:bg-blue-100 text-blue-700 rounded"><Mail size={14} /></button>
                        {!['fatturato', 'annullato'].includes(c.stato) && (
                          <>
                            {c.stato !== 'pagato' && (
                              <button onClick={() => setShowPagamento(c)} title="Segna come pagato" data-testid={`conto-pay-${c.id}`}
                                className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded"><CheckCircle2 size={14} /></button>
                            )}
                            <button onClick={() => setShowConverti(c)} title="Converti in Fattura" data-testid={`conto-converti-${c.id}`}
                              className="p-1.5 hover:bg-purple-100 text-purple-700 rounded"><FileSignature size={14} /></button>
                            <button onClick={() => openEdit(c)} title="Modifica" data-testid={`conto-edit-${c.id}`}
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Edit2 size={14} /></button>
                          </>
                        )}
                        <button onClick={() => duplica(c)} title="Duplica" data-testid={`conto-copy-${c.id}`}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Copy size={14} /></button>
                        <RowActions
                          entity="conti"
                          id={c.id}
                          label={`il conto ${c.numero_conto}`}
                          onChange={load}
                          compact
                        />
                        {c.stato !== 'fatturato' && c.stato !== 'annullato' && (
                          <button onClick={() => annulla(c)} title="Annulla conto" data-testid={`conto-annulla-${c.id}`}
                            className="p-1.5 hover:bg-red-100 text-red-600 rounded"><X size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Paginazione */}
        {total > LIMIT && (
          <div className="flex items-center justify-between p-3 border-t border-slate-200 bg-slate-50 text-xs">
            <span className="text-slate-600">Mostrando {skip + 1}–{Math.min(skip + LIMIT, total)} di {total}</span>
            <div className="flex items-center gap-1">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))}
                className="p-1.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronLeft size={14} /></button>
              <span className="font-bold px-2">{currentPage}/{totalPages}</span>
              <button disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)}
                className="p-1.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Modali */}
      {showForm && (
        <ContoFormModal
          editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {showConverti && (
        <ConvertiFatturaModal
          conto={showConverti}
          onClose={() => setShowConverti(null)}
          onConverted={(fatt) => {
            setShowConverti(null);
            load();
            alert(`✓ Convertito in ${fatt.tipo_documento === 'TD24' ? 'ricevuta fiscale' : 'fattura'} ${fatt.numero_fattura}`);
          }}
        />
      )}
      {showPagamento && (
        <PagamentoModal
          conto={showPagamento}
          onClose={() => setShowPagamento(null)}
          onSaved={() => { setShowPagamento(null); load(); }}
        />
      )}
      {showEmail && (
        <EmailModal
          conto={showEmail}
          onClose={() => setShowEmail(null)}
          onSent={() => { setShowEmail(null); load(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MODALE: Form Crea/Modifica Conto
// ───────────────────────────────────────────────────────────────────────────
function ContoFormModal({ editing, onClose, onSaved }) {
  const [clienti, setClienti] = useState([]);
  const [regimiIvaZero, setRegimiIvaZero] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clienteMode, setClienteMode] = useState(editing?.cliente_id ? 'existing' : 'new');
  const [capComuni, setCapComuni] = useState([]);
  const [capMsg, setCapMsg] = useState('');
  const [form, setForm] = useState(() => editing ? {
    cliente_id: editing.cliente_id || '',
    cliente_nuovo: null,
    oggetto: editing.oggetto || '',
    applica_iva: editing.applica_iva !== false,
    iva_default: editing.iva_default || 22,
    sconto_globale_pct: editing.sconto_globale_pct || 0,
    note: editing.note || '',
    righe: (editing.righe || []).map(r => ({ ...r })),
  } : {
    cliente_id: '',
    cliente_nuovo: { tipo_cliente: 'persona_fisica', nome: '', cognome: '', email: '', telefono: '', indirizzo: '', numero_civico: '', cap: '', comune: '', provincia: '' },
    oggetto: '',
    applica_iva: true,
    iva_default: 22,
    sconto_globale_pct: 0,
    note: '',
    righe: [{ descrizione: '', quantita: 1, unita_misura: 'pz', prezzo_unitario: 0, sconto_pct: 0, aliquota_iva: 22 }],
  });

  useEffect(() => {
    API.get('/api/clienti').then(r => setClienti(r.data || [])).catch(() => {});
    API.get('/api/sdi/regimi-iva-zero').then(r => setRegimiIvaZero(r.data?.regimi || [])).catch(() => {});
  }, []);

  const updRiga = (i, k, v) => {
    const rr = [...form.righe];
    rr[i] = { ...rr[i], [k]: v };
    if (k === 'aliquota_iva' && Number(v) > 0) {
      rr[i].natura = ''; rr[i].riferimento_normativo = ''; rr[i]._regime_key = '';
    }
    setForm({ ...form, righe: rr });
  };

  const applicaRegime = (i, regKey) => {
    const reg = regimiIvaZero.find(r => r.key === regKey);
    const rr = [...form.righe];
    if (!regKey || !reg) {
      rr[i] = { ...rr[i], aliquota_iva: 22, natura: '', riferimento_normativo: '', _regime_key: '' };
      setForm({ ...form, righe: rr });
      return;
    }
    rr[i] = { ...rr[i], aliquota_iva: 0, natura: reg.natura, riferimento_normativo: reg.dicitura, _regime_key: reg.key };
    const noteAttuali = (form.note || '').trim();
    const nuoveNote = (reg.dicitura && !noteAttuali.includes(reg.dicitura.slice(0, 30)))
      ? (noteAttuali ? `${noteAttuali}\n${reg.dicitura}` : reg.dicitura)
      : noteAttuali;
    setForm({ ...form, righe: rr, note: nuoveNote });
  };

  const addRiga = () => setForm({ ...form, righe: [...form.righe, { descrizione: '', quantita: 1, unita_misura: 'pz', prezzo_unitario: 0, sconto_pct: 0, aliquota_iva: form.iva_default }] });
  const delRiga = (i) => setForm({ ...form, righe: form.righe.filter((_, x) => x !== i) });

  const handleCapNuovo = async (raw) => {
    const cap = (raw || '').replace(/\D/g, '').slice(0, 5);
    setForm(f => ({ ...f, cliente_nuovo: { ...f.cliente_nuovo, cap } }));
    setCapComuni([]); setCapMsg('');
    if (cap.length !== 5) return;
    try {
      const { data } = await API.get(`/api/geo/cap/${cap}`);
      const list = data.comuni || [];
      if (list.length === 1) {
        setForm(f => ({ ...f, cliente_nuovo: { ...f.cliente_nuovo, cap, comune: list[0].comune, provincia: list[0].sigla } }));
      } else if (list.length > 1) {
        setCapComuni(list);
        setCapMsg(`${list.length} comuni per questo CAP: seleziona quello corretto`);
      } else {
        setCapMsg('CAP non trovato: inserisci Comune e Provincia manualmente');
      }
    } catch {
      setCapMsg('Lookup CAP non disponibile: inserisci Comune e Provincia manualmente');
    }
  };
  const pickComuneNuovo = (c) => {
    setForm(f => ({ ...f, cliente_nuovo: { ...f.cliente_nuovo, comune: c.comune, provincia: c.sigla } }));
    setCapComuni([]); setCapMsg('');
  };

  // Live totali UI
  const sc = Math.max(0, Math.min(100, parseFloat(form.sconto_globale_pct) || 0));
  const applicaIva = form.applica_iva !== false;
  const impLordo = form.righe.reduce((s, r) => s + (parseFloat(r.quantita || 0) * parseFloat(r.prezzo_unitario || 0) * (1 - (parseFloat(r.sconto_pct || 0) / 100))), 0);
  const scontoImp = impLordo * sc / 100;
  const impNetto = impLordo - scontoImp;
  const ivaTot = applicaIva
    ? form.righe.reduce((s, r) => {
        const rTot = parseFloat(r.quantita || 0) * parseFloat(r.prezzo_unitario || 0) * (1 - (parseFloat(r.sconto_pct || 0) / 100));
        const ali = parseFloat(r.aliquota_iva || 0);
        return s + (rTot * (1 - sc / 100) * ali / 100);
      }, 0)
    : 0;
  const totFin = impNetto + ivaTot;

  const save = async () => {
    if (clienteMode === 'existing' && !form.cliente_id) { alert('Seleziona un cliente o passa a "Nuovo"'); return; }
    if (clienteMode === 'new' && !(form.cliente_nuovo?.nome || form.cliente_nuovo?.ragione_sociale)) {
      alert('Inserisci almeno nome o ragione sociale del nuovo cliente'); return;
    }
    if (form.righe.length === 0) { alert('Aggiungi almeno una riga'); return; }
    // Validazione IVA 0% senza natura
    const missingNatura = form.righe.filter(r => Number(r.aliquota_iva) === 0 && !r.natura);
    if (missingNatura.length > 0) {
      if (!window.confirm(`${missingNatura.length} riga/righe con IVA 0% non hanno una Natura selezionata. L'XML SDI verrà rifiutato. Continuare comunque?`)) return;
    }
    setSaving(true);
    try {
      const payload = {
        oggetto: form.oggetto,
        applica_iva: form.applica_iva,
        iva_default: form.iva_default,
        sconto_globale_pct: form.sconto_globale_pct,
        note: form.note,
        righe: form.righe,
      };
      if (clienteMode === 'existing') {
        payload.cliente_id = form.cliente_id;
      } else {
        payload.cliente_nuovo = form.cliente_nuovo;
      }
      if (editing?.id) {
        await API.put(`/api/conti/${editing.id}`, payload);
      } else {
        await API.post('/api/conti', payload);
      }
      onSaved();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="conto-form-modal">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-black flex items-center gap-2">
            <Receipt size={20} className="text-primary" />
            {editing?.id ? `Modifica Conto ${editing.numero_conto || ''}` : 'Nuovo Conto'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Cliente */}
          <section className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-3 mb-3">
              <label className="text-sm font-bold">Cliente</label>
              <div className="flex bg-white border border-slate-200 rounded p-0.5">
                <button onClick={() => setClienteMode('existing')} data-testid="cliente-mode-existing"
                  className={`px-3 py-1 text-xs font-bold rounded transition-colors ${clienteMode === 'existing' ? 'bg-primary text-white' : 'text-slate-600'}`}>
                  Esistente
                </button>
                <button onClick={() => setClienteMode('new')} data-testid="cliente-mode-new"
                  className={`px-3 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${clienteMode === 'new' ? 'bg-primary text-white' : 'text-slate-600'}`}>
                  <UserPlus size={12} /> Nuovo
                </button>
              </div>
            </div>
            {clienteMode === 'existing' ? (
              <select value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded" data-testid="cliente-select">
                <option value="">— Seleziona cliente —</option>
                {clienti.map(c => {
                  const label = c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim();
                  return <option key={c.id} value={c.id}>{label}</option>;
                })}
              </select>
            ) : (
              <div className="grid grid-cols-12 gap-2">
                <select value={form.cliente_nuovo?.tipo_cliente || 'persona_fisica'} onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, tipo_cliente: e.target.value } })}
                  className="col-span-3 text-xs px-2 py-2 border border-slate-300 rounded" data-testid="nuovo-cli-tipo">
                  <option value="persona_fisica">Persona fisica</option>
                  <option value="azienda">Azienda</option>
                  <option value="condominio">Condominio</option>
                </select>
                {form.cliente_nuovo?.tipo_cliente === 'azienda' || form.cliente_nuovo?.tipo_cliente === 'condominio' ? (
                  <input placeholder="Ragione sociale *" value={form.cliente_nuovo?.ragione_sociale || ''}
                    onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, ragione_sociale: e.target.value } })}
                    className="col-span-9 text-sm px-2 py-2 border border-slate-300 rounded" data-testid="nuovo-cli-rs" />
                ) : (
                  <>
                    <input placeholder="Nome *" value={form.cliente_nuovo?.nome || ''}
                      onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, nome: e.target.value } })}
                      className="col-span-4 text-sm px-2 py-2 border border-slate-300 rounded" data-testid="nuovo-cli-nome" />
                    <input placeholder="Cognome" value={form.cliente_nuovo?.cognome || ''}
                      onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, cognome: e.target.value } })}
                      className="col-span-5 text-sm px-2 py-2 border border-slate-300 rounded" />
                  </>
                )}
                <input placeholder="Telefono" value={form.cliente_nuovo?.telefono || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, telefono: e.target.value } })}
                  className="col-span-4 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="Email" value={form.cliente_nuovo?.email || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, email: e.target.value } })}
                  className="col-span-4 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="P.IVA / CF" value={form.cliente_nuovo?.cf || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, cf: e.target.value, piva: e.target.value } })}
                  className="col-span-4 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="Indirizzo" value={form.cliente_nuovo?.indirizzo || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, indirizzo: e.target.value } })}
                  className="col-span-4 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="Civico" value={form.cliente_nuovo?.numero_civico || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, numero_civico: e.target.value } })}
                  className="col-span-2 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="CAP" data-testid="conto-cliente-cap" value={form.cliente_nuovo?.cap || ''}
                  onChange={(e) => handleCapNuovo(e.target.value)}
                  className="col-span-2 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="Comune" data-testid="conto-cliente-comune" value={form.cliente_nuovo?.comune || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, comune: e.target.value } })}
                  className="col-span-3 text-sm px-2 py-2 border border-slate-300 rounded" />
                <input placeholder="Prov" maxLength={2} data-testid="conto-cliente-provincia" value={form.cliente_nuovo?.provincia || ''}
                  onChange={(e) => setForm({ ...form, cliente_nuovo: { ...form.cliente_nuovo, provincia: e.target.value.toUpperCase().slice(0, 2) } })}
                  className="col-span-1 text-sm px-2 py-2 border border-slate-300 rounded uppercase" />
                {(capComuni.length > 0 || capMsg) && (
                  <div className="col-span-12 rounded-md border border-amber-200 bg-amber-50 p-2" data-testid="conto-cap-suggestions">
                    {capMsg && <p className="text-xs text-amber-800 mb-1">{capMsg}</p>}
                    {capComuni.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {capComuni.map((c, i) => (
                          <button key={i} type="button" data-testid={`conto-cap-comune-opt-${i}`} onClick={() => pickComuneNuovo(c)}
                            className="px-2.5 py-1 text-xs rounded-full border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 font-medium transition-colors">
                            {c.comune} ({c.sigla})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="col-span-12 text-[10px] text-amber-700 italic">⚡ Il nuovo cliente verrà creato in anagrafica al salvataggio</div>
              </div>
            )}
          </section>

          {/* Oggetto + IVA/Sconto */}
          <section className="grid grid-cols-12 gap-3">
            <div className="col-span-12">
              <label className="text-xs font-bold text-slate-600">Oggetto / Descrizione lavoro</label>
              <input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
                placeholder="Es. Riparazione perdita urgente bagno"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded" data-testid="oggetto-input" />
            </div>
            <div className="col-span-12 sm:col-span-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded p-2">
              <input type="checkbox" checked={form.applica_iva !== false}
                onChange={(e) => setForm({ ...form, applica_iva: e.target.checked })} data-testid="applica-iva" />
              <label className="text-xs font-bold">Applica IVA</label>
              {!form.applica_iva && <span className="text-[9px] text-amber-700 bg-amber-200 px-1 rounded font-bold">no IVA</span>}
            </div>
            <div className="col-span-12 sm:col-span-4">
              <label className="text-xs font-bold text-slate-600">IVA default %</label>
              <select value={form.iva_default} onChange={(e) => {
                const nuovaIva = parseFloat(e.target.value);
                // Propaga la nuova IVA di default a tutte le righe che non
                // hanno una natura di esenzione (reverse charge, ecc.) e
                // che avevano ancora la vecchia IVA di default.
                const oldIva = form.iva_default;
                const righeAgg = (form.righe || []).map(r => {
                  if (r.natura || r._regime_key) return r; // esente: non toccare
                  const cur = Number(r.aliquota_iva);
                  // aggiorna se corrisponde alla vecchia default (o e' 0/undefined su nuove righe)
                  if (cur === Number(oldIva) || !cur) {
                    return { ...r, aliquota_iva: nuovaIva };
                  }
                  return r;
                });
                setForm({ ...form, iva_default: nuovaIva, righe: righeAgg });
              }}
                className="w-full px-2 py-2 text-sm border border-slate-300 rounded" data-testid="iva-default">
                <option value={22}>22%</option>
                <option value={20}>20%</option>
                <option value={10}>10%</option>
                <option value={4}>4%</option>
                <option value={0}>0%</option>
              </select>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <label className="text-xs font-bold text-slate-600">Sconto globale %</label>
              <input type="number" min={0} max={100} step={0.1} value={form.sconto_globale_pct}
                onChange={(e) => setForm({ ...form, sconto_globale_pct: e.target.value })}
                className="w-full px-2 py-2 text-sm border border-slate-300 rounded text-right font-mono" data-testid="sconto-globale" />
            </div>
          </section>

          {/* Righe */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold">Righe</label>
              <div className="flex items-center gap-2">
                {regimiIvaZero.find(x => x.key === 'reverse_edile_servizi') && (
                  <button onClick={() => {
                    const reg = regimiIvaZero.find(x => x.key === 'reverse_edile_servizi');
                    const rr = form.righe.map(r => ({ ...r, aliquota_iva: 0, natura: reg.natura, riferimento_normativo: reg.dicitura, _regime_key: reg.key }));
                    const dic = reg.dicitura;
                    const noteAttuali = (form.note || '').trim();
                    const nuoveNote = (dic && !noteAttuali.includes(dic.slice(0, 30))) ? (noteAttuali ? `${noteAttuali}\n${dic}` : dic) : noteAttuali;
                    setForm({ ...form, righe: rr, note: nuoveNote });
                  }} data-testid="apply-reverse-edile-btn"
                    className="text-[10px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2 py-1 rounded"
                    title="Applica Reverse Charge Edile a tutte le righe (N6.7 + dicitura)">
                    ⚡ Reverse Charge Edile
                  </button>
                )}
                <button onClick={addRiga} data-testid="riga-add"
                  className="text-xs font-bold text-primary hover:underline flex items-center gap-1"><Plus size={12} /> aggiungi</button>
              </div>
            </div>
            {form.righe.map((r, i) => {
              const isZero = Number(r.aliquota_iva) === 0;
              return (
                <div key={i} className="space-y-1">
                  <div className="grid grid-cols-12 gap-2 items-start" data-testid={`riga-${i}`}>
                    <input placeholder="Descrizione" value={r.descrizione || ''} onChange={(e) => updRiga(i, 'descrizione', e.target.value)}
                      className="col-span-4 px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid={`riga-${i}-desc`} />
                    <input type="number" step="0.01" placeholder="Q.tà" value={r.quantita} onChange={(e) => updRiga(i, 'quantita', e.target.value)}
                      className="col-span-1 px-2 py-1.5 text-sm border border-slate-300 rounded text-right" />
                    <input placeholder="U.M." value={r.unita_misura || ''} onChange={(e) => updRiga(i, 'unita_misura', e.target.value)}
                      className="col-span-1 px-2 py-1.5 text-sm border border-slate-300 rounded" />
                    <input type="number" step="0.01" placeholder="Prezzo €" value={r.prezzo_unitario} onChange={(e) => updRiga(i, 'prezzo_unitario', e.target.value)}
                      className="col-span-2 px-2 py-1.5 text-sm border border-slate-300 rounded text-right font-mono" />
                    <input type="number" step="0.1" placeholder="Sc%" value={r.sconto_pct || 0} onChange={(e) => updRiga(i, 'sconto_pct', e.target.value)}
                      className="col-span-1 px-2 py-1.5 text-sm border border-slate-300 rounded text-right" />
                    <input type="number" placeholder="IVA%" value={r.aliquota_iva} onChange={(e) => updRiga(i, 'aliquota_iva', e.target.value)}
                      className="col-span-2 px-2 py-1.5 text-sm border border-slate-300 rounded text-right" data-testid={`riga-${i}-iva`} />
                    <button onClick={() => delRiga(i)} className="col-span-1 p-1.5 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {isZero && (
                    <div className="ml-[33.33%] p-2 bg-amber-50 border border-amber-200 rounded text-xs flex items-start gap-2" data-testid={`riga-${i}-natura-box`}>
                      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-amber-900">IVA 0% — regime:</span>
                          <select value={r._regime_key || ''} onChange={(e) => applicaRegime(i, e.target.value)}
                            className="text-xs px-2 py-1 border border-amber-300 rounded bg-white max-w-md" data-testid={`riga-${i}-regime`}>
                            <option value="">— scegli regime —</option>
                            {regimiIvaZero.map(reg => (
                              <option key={reg.key} value={reg.key}>{reg.natura} · {reg.label}</option>
                            ))}
                          </select>
                        </div>
                        {r.riferimento_normativo && (
                          <div className="text-[10px] text-amber-800 italic">📜 {r.riferimento_normativo}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* Note + Totali */}
          <section className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-7">
              <label className="text-xs font-bold text-slate-600">Note (incluse nel PDF e nella Causale SDI)</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={4} className="w-full px-3 py-2 text-sm border border-slate-300 rounded font-mono"
                placeholder="Es. dicitura legale, modalità di pagamento, garanzie…" data-testid="note-input" />
            </div>
            <div className="col-span-12 sm:col-span-5 bg-slate-50 border border-slate-200 rounded p-3 space-y-1 text-sm">
              {sc > 0 && (
                <>
                  <div className="flex justify-between"><span>Imp. lordo:</span><span className="font-mono">{fmt(impLordo)}</span></div>
                  <div className="flex justify-between text-red-600"><span>Sconto {sc}%:</span><span className="font-mono">− {fmt(scontoImp)}</span></div>
                </>
              )}
              <div className="flex justify-between"><span>Imponibile:</span><span className="font-mono font-bold">{fmt(impNetto)}</span></div>
              {form.applica_iva
                ? <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{fmt(ivaTot)}</span></div>
                : <div className="flex justify-between text-amber-700"><span>IVA:</span><span className="italic">non applicata</span></div>}
              <div className="pt-1 border-t border-slate-300 flex justify-between text-base">
                <span className="font-black">TOTALE:</span>
                <span className="font-mono font-black text-primary" data-testid="totale-finale">{fmt(totFin)}</span>
              </div>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="save-conto-btn"
            className="px-4 py-1.5 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded disabled:opacity-50">
            {saving ? 'Salvataggio…' : (editing?.id ? 'Salva modifiche' : 'Crea Conto')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MODALE: Conversione in Fattura / Ricevuta
// ───────────────────────────────────────────────────────────────────────────
function ConvertiFatturaModal({ conto, onClose, onConverted }) {
  const [tipo, setTipo] = useState('fattura');
  const [dataEm, setDataEm] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(`/api/conti/${conto.id}/converti-fattura`, { tipo_fattura: tipo, data_emissione: dataEm });
      onConverted(data);
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="converti-modal">
      <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-base font-black flex items-center gap-2"><FileSignature size={18} className="text-purple-600" /> Converti in Fattura</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-500">Conto <b>{conto.numero_conto}</b> — Totale: <b>{fmt(conto.totale_finale)}</b></p>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-2 block">Tipo documento</label>
            <div className="space-y-2">
              <button onClick={() => setTipo('fattura')}
                className={`w-full text-left p-3 rounded border-2 transition-colors ${tipo === 'fattura' ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid="tipo-fattura">
                <div className="font-bold text-sm">Fattura Ordinaria (TD01)</div>
                <p className="text-xs text-slate-500 mt-0.5">Documento fiscale standard B2B/B2C con SDI</p>
              </button>
              <button onClick={() => setTipo('ricevuta')}
                className={`w-full text-left p-3 rounded border-2 transition-colors ${tipo === 'ricevuta' ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid="tipo-ricevuta">
                <div className="font-bold text-sm">Ricevuta Fiscale (TD24)</div>
                <p className="text-xs text-slate-500 mt-0.5">Per privati senza P.IVA / regime forfettario</p>
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600">Data emissione</label>
            <input type="date" value={dataEm} onChange={(e) => setDataEm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded">Annulla</button>
          <button onClick={submit} disabled={loading} data-testid="converti-confirm"
            className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded disabled:opacity-50">
            {loading ? 'Conversione…' : 'Crea Fattura'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MODALE: Segna come pagato (con metodo)
// ───────────────────────────────────────────────────────────────────────────
function PagamentoModal({ conto, onClose, onSaved }) {
  const [metodo, setMetodo] = useState('contanti');
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await API.post(`/api/conti/${conto.id}/marca-pagato`, {
        metodo_pagamento: metodo,
        data_pagamento: new Date(data).toISOString(),
      });
      onSaved();
    } catch (e) {
      alert('Errore: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pagamento-modal">
      <div className="bg-white rounded-lg max-w-sm w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-base font-black flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> Conto pagato</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-500">Conto <b>{conto.numero_conto}</b> — Importo: <b>{fmt(conto.totale_finale)}</b></p>
          <div>
            <label className="text-xs font-bold text-slate-600">Metodo di pagamento</label>
            <select value={metodo} onChange={(e) => setMetodo(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" data-testid="metodo-pagamento">
              <option value="contanti">Contanti</option>
              <option value="bonifico">Bonifico bancario</option>
              <option value="assegno">Assegno</option>
              <option value="pos">POS / Carta</option>
              <option value="altro">Altro</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600">Data incasso</label>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded">Annulla</button>
          <button onClick={submit} disabled={loading} data-testid="pagamento-confirm"
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded disabled:opacity-50">
            {loading ? '…' : 'Segna pagato'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// MODALE: Invia email PDF al cliente
// ───────────────────────────────────────────────────────────────────────────
function EmailModal({ conto, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(`Conto ${conto.numero_conto} - EDIL CIMA S.R.L.`);
  const [messaggio, setMessaggio] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Prefill destinatario da cliente del conto
    if (conto.cliente_id) {
      API.get(`/api/clienti/${conto.cliente_id}`).then(r => {
        if (r.data?.email) setTo(r.data.email);
      }).catch(() => {});
    }
  }, [conto.cliente_id]);

  const submit = async () => {
    if (!to) { alert('Inserisci il destinatario'); return; }
    setLoading(true);
    try {
      await API.post(`/api/conti/${conto.id}/email`, {
        to: to.split(',').map(s => s.trim()).filter(Boolean),
        subject,
        messaggio,
      });
      onSent();
    } catch (e) {
      alert('Errore invio: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-modal">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-base font-black flex items-center gap-2"><Mail size={18} className="text-blue-600" /> Invia PDF al cliente</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-600">Destinatario (separa più indirizzi con virgola)</label>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="cliente@email.it"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" data-testid="email-to" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600">Oggetto</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600">Messaggio (opzionale)</label>
            <textarea value={messaggio} onChange={(e) => setMessaggio(e.target.value)} rows={4}
              placeholder="Gentile cliente, in allegato il conto…"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
          </div>
          <p className="text-[10px] text-slate-500">📎 Il PDF del conto sarà allegato automaticamente. Il conto verrà marcato come "Inviato".</p>
        </div>
        <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded">Annulla</button>
          <button onClick={submit} disabled={loading} data-testid="email-send"
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded disabled:opacity-50 inline-flex items-center gap-1">
            {loading ? '…' : <><Send size={14} /> Invia</>}
          </button>
        </div>
      </div>
    </div>
  );
}
